import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { VendorPayoutNotificationBanner, VendorPayoutOnboardingDialog, VendorPayoutManagementDialog } from "@/components/vendor-payout-embedded";
import { AlertCircle } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PaymentTransaction, formatCents, formatDate, statusColors } from "./_shared";
import { TeamMemberRatesSection } from "./TeamMemberRates";



// ── Payments Tab ──────────────────────────────────────────────────────────────

export function PaymentsTab({ vendorId, isOwner }: { vendorId: number; isOwner: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const { data: payments = [], isLoading } = useQuery<PaymentTransaction[]>({
    queryKey: ["vendor", vendorId, "payments"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/payments", { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
  });

  const { data: payoutAccount } = useQuery<{ stripeConnectId?: string; status?: string; defaultPayoutSpeed?: string; disabledReason?: string | null } | null>({
    queryKey: ["vendor", vendorId, "payout-account"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/payout-account", { credentials: "include" });
      if (!r.ok) return null;
      return r.json();
    },
    enabled: isOwner,
    // Task #1636 — Poll while the Payments tab is open so the
    // `account.updated` webhook's status flip lands within ~10s without
    // the vendor having to re-open the Stripe dialog or refresh the page.
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });

  const payoutMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ id, method }: { id: number; method: "standard" | "instant" }) => {
      const r = await apiRequest("POST", `/api/vendor-portal/payments/${id}/payout`, { method });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "payments"] });
      toast({ title: "Payout requested" });
    },
    onError: (e: any) => toast({ title: "Payout failed", description: parseErrorMessage(e, "Try again."), variant: "destructive" }),
  });

  const isFullyOnboarded = payoutAccount?.status === "active";

  const handleConnectClick = () => {
    // Only route fully-onboarded accounts to the management surface. Accounts
    // that exist in Stripe but still have outstanding requirements (pending /
    // restricted / etc.) must continue through the onboarding component so the
    // vendor can finish the remaining steps.
    if (isFullyOnboarded) {
      setManageOpen(true);
    } else {
      setOnboardingOpen(true);
    }
  };

  const handleOnboardingExit = () => {
    // Refresh the payout account status from Stripe after the vendor closes
    // the embedded onboarding flow so the UI reflects the new state.
    fetch("/api/vendor-portal/payout-account/refresh-status", { method: "POST", credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((updated) => {
        if (updated) queryClient.setQueryData(["vendor", vendorId, "payout-account"], updated);
        queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "payout-account"] });
      })
      .catch(() => {
        queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "payout-account"] });
      });
  };

  const isConnected = payoutAccount?.status === "active" || payoutAccount?.stripeConnectId;
  const isRestricted = payoutAccount?.status === "restricted";
  // Task #1636 — Switch the banner palette to red when Stripe has paused
  // payouts so a restricted vendor sees the change in real time (the
  // payout-account query polls every 10s) without re-opening the dialog.
  const bannerToneClass = isRestricted
    ? "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800"
    : "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800";
  const bannerIconClass = isRestricted ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400";
  const bannerHeadingClass = isRestricted ? "text-red-800 dark:text-red-200" : "text-amber-800 dark:text-amber-200";
  const bannerBodyClass = isRestricted ? "text-red-700 dark:text-red-300" : "text-amber-700 dark:text-amber-300";
  const bannerDividerClass = isRestricted ? "border-red-200 dark:border-red-800" : "border-amber-200 dark:border-amber-800";

  let headline = "Direct Payouts via Stripe Connect";
  let body: string;
  if (isRestricted) {
    headline = "Payouts paused — action required";
    body = payoutAccount?.disabledReason
      ? `Stripe paused your payouts (${payoutAccount.disabledReason}). Update your payout account to resume receiving money.`
      : "Stripe paused your payouts. Update your payout account to resume receiving money.";
  } else if (isFullyOnboarded) {
    body = "Your Stripe Connect account is active. You can update your bank details or business info anytime.";
  } else if (isConnected) {
    body = "Your Stripe Connect account isn't fully approved yet. Open it to finish the remaining steps.";
  } else {
    body = "Set up direct bank payouts by connecting your Stripe account.";
  }
  const ctaLabel = isRestricted ? "Fix now" : isFullyOnboarded ? "Update" : isConnected ? "Finish setup" : "Connect";

  return (
    <div className="space-y-3">
      {isOwner && (
        <div
          className={`${bannerToneClass} border rounded-lg p-3 space-y-3`}
          data-tour="payments-connect-cta"
          data-testid={`banner-payout-status-${payoutAccount?.status ?? "none"}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2">
              <AlertCircle className={`h-4 w-4 ${bannerIconClass} mt-0.5 flex-shrink-0`} />
              <div>
                <p className={`text-xs font-medium ${bannerHeadingClass}`} data-testid="text-payout-status-headline">{headline}</p>
                <p className={`text-xs ${bannerBodyClass} mt-0.5`} data-testid="text-payout-status-body">
                  {body}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 text-xs"
              onClick={handleConnectClick}
              data-testid="button-stripe-connect"
            >
              {ctaLabel}
            </Button>
          </div>
          {isOwner && isConnected && (
            <div className={`border-t ${bannerDividerClass} pt-3`}>
              <VendorPayoutNotificationBanner />
            </div>
          )}
          {isConnected && (
            <div className={`border-t ${bannerDividerClass} pt-3`} data-tour="payments-payout-speed">
              <p className={`text-xs ${bannerBodyClass}`}>
                Payments are paid out automatically to your bank account each business day via Stripe's standard daily payout cadence. You can also request an instant payout on any settled payment for a 1% fee.
              </p>
            </div>
          )}
        </div>
      )}
      {isLoading ? (
        <p className="text-sm text-gray-400 text-center py-8">Loading payments...</p>
      ) : payments.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">No payment records yet.</p>
      ) : (() => {
        // Group payments by month
        const groups: Record<string, PaymentTransaction[]> = {};
        for (const p of payments) {
          const key = p.createdAt ? new Date(p.createdAt).toLocaleString("default", { month: "long", year: "numeric" }) : "Unknown";
          if (!groups[key]) groups[key] = [];
          groups[key].push(p);
        }
        let isFirstRow = true;
        return (
          <div className="space-y-3" data-tour="payments-history-list">
          {Object.entries(groups).map(([month, txns]) => {
          const gross = txns.reduce((s, t) => s + (t.vendorAmountCents || 0), 0);
          const fees = txns.reduce((s, t) => s + (t.stripeFeeCents || 0), 0);
          const net = gross - fees;
          return (
            <div key={month} className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{month}</p>
                <div className="text-xs text-gray-500 flex gap-3">
                  <span>Gross: <span className="font-medium text-gray-700">{formatCents(gross)}</span></span>
                  {fees > 0 && <span>PadPulse fees: <span className="font-medium text-red-600">-{formatCents(fees)}</span></span>}
                  <span>Net: <span className="font-medium text-green-700">{formatCents(net)}</span></span>
                </div>
              </div>
              {txns.map(payment => {
                const settled = payment.status === "succeeded" || payment.status === "settled" || payment.status === "paid";
                const showInstant = isOwner && isConnected && settled;
                const instantFeeCents = Math.max(1, Math.round((payment.vendorAmountCents || 0) * 0.01));
                const instantNetCents = (payment.vendorAmountCents || 0) - instantFeeCents;
                const tourTag = isFirstRow ? "payments-history-row" : undefined;
                if (isFirstRow) isFirstRow = false;
                return (
                  <Card key={payment.id} data-testid={`card-vendor-payment-${payment.id}`} data-tour={tourTag}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">
                            {payment.taskId ? `Task #${payment.taskId}` : `Payment #${payment.id}`}
                          </p>
                          {payment.propertyId && (
                            <p className="text-xs text-gray-600" data-testid={`text-payment-property-${payment.id}`}>
                              {payment.propertyAddress || payment.propertyName || `Property #${payment.propertyId}`}
                            </p>
                          )}
                          <p className="text-xs text-gray-400">{formatDate(payment.createdAt)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-sm">{formatCents(payment.vendorAmountCents)}</p>
                          {(payment.stripeFeeCents || 0) > 0 && (
                            <p className="text-xs text-gray-400">PadPulse fee: {formatCents(payment.stripeFeeCents)}</p>
                          )}
                          <Badge className={statusColors[payment.status || ""] || "bg-gray-100 text-gray-800"} variant="secondary">
                            {payment.status || "pending"}
                          </Badge>
                        </div>
                      </div>
                      {showInstant && (
                        <div className="mt-2 pt-2 border-t flex items-center justify-between gap-2">
                          <p className="text-[11px] text-gray-500">
                            Instant: net <span className="font-medium text-gray-700">{formatCents(instantNetCents)}</span>
                            <span className="text-gray-400"> (−{formatCents(instantFeeCents)} 1% fee)</span>
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            disabled={payoutMutation.isPending}
                            onClick={() => payoutMutation.mutate({ id: payment.id, method: "instant" })}
                            data-testid={`button-instant-payout-${payment.id}`}
                          >
                            Pay out instantly
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          );
        })}
          </div>
        );
      })()}
      {/* Task #2783 — Per-team-member rates (owner-only) */}
      {isOwner && (
        <TeamMemberRatesSection vendorId={vendorId} queryClient={queryClient} toast={toast} />
      )}
      {isOwner && (
        <>
          <VendorPayoutOnboardingDialog
            open={onboardingOpen}
            onOpenChange={setOnboardingOpen}
            onExit={handleOnboardingExit}
          />
          <VendorPayoutManagementDialog
            open={manageOpen}
            onOpenChange={(o) => {
              setManageOpen(o);
              if (!o) queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "payout-account"] });
            }}
          />
        </>
      )}
    </div>
  );
}
